import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Flex,
  Form,
  Heading,
  Modal,
  Paragraph,
  useToast,
} from "@ir/ui";
import {
  CreateConfigPortalCredentialDTO,
  DeleteConfigPortalCredentialDTO,
  UpdateConfigPortalCredentialDTO,
} from "@src/domain/dto/config";
import { GetMasterDataPurchasersDTO } from "@src/domain/dto/masterData";
import type { ConfigPortalCredential } from "@src/domain/entities/config";
import type { PurchaserPortalKey } from "@src/domain/entities/purchaser";
import {
  CreateConfigPortalCredentialUseCase,
  DeleteConfigPortalCredentialUseCase,
  GetPortalKeysByPurchaserKeyUseCase,
  UpdateConfigPortalCredentialUseCase,
} from "@src/application/useCases/config";
import { GetMasterDataPurchasersUseCase } from "@src/application/useCases/masterData";
import { ConfigGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/ConfigGraphQlAdapter";
import { ConfigPortalCredentialRepository } from "@src/infrastructure/repositories/config";
import { MasterDataPurchaserRepository } from "@src/infrastructure/repositories/masterData";
import { invalidateConfigListCacheScopes } from "@src/presentation/providers/config/configListCache";
import { useConfigPortalCredentials } from "@src/presentation/providers/config/ConfigPortalCredentialsDataProvider";
import { useConfigBrand } from "../ConfigBrandContext";
import { ConfigAddEditModal } from "../utils/ConfigAddEditModal";
import {
  formatConfigRecordCount,
  useConfigAddEditModal,
} from "../utils/useConfigAddEditModal";
import { getConfigSaveErrorMessage } from "../utils/configSaveErrors";
import {
  ConfigPortalCredentialAddEditForm,
  type PortalCredentialAddEditFormValues,
  type PortalKeyOption,
  type PurchaserOption,
} from "../utils/ConfigPortalCredentialAddEditForm";
import { ConfigPortalCredentialsTable } from "../utils/ConfigPortalCredentialsTable";
import { ConfigWritableListLayout } from "../utils/ConfigWritableListLayout";
import { MasterDataJsonPreviewModal } from "@src/presentation/pages/MasterData/utils/MasterDataJsonPreviewModal";
import {
  formatJsonForTextarea,
  parseOptionalJsonField,
} from "@src/presentation/pages/Purchasers/utils/purchaserConfigTemplateFormUtils";

const EMPTY_FORM_VALUES: PortalCredentialAddEditFormValues = {
  purchaserId: null,
  portalKey: "",
  portalUserName: "",
  portalPassword: "",
  isActive: true,
  metadata: "",
};

export const ConfigPortalCredentialsPage = () => {
  const { brandKey } = useConfigBrand();
  const {
    credentials,
    loading,
    search,
    setSearch,
    total,
    setPage,
    refetch,
  } = useConfigPortalCredentials();
  const { success: successToast, error: errorToast } = useToast();
  const [form] = Form.useForm<PortalCredentialAddEditFormValues>();

  const {
    modalMode,
    editingEntity: editingCredential,
    isSaving,
    setIsSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    closeModal: closeModalState,
    openCreateModal,
    openEditModal,
  } = useConfigAddEditModal<ConfigPortalCredential>();

  const [purchaserOptions, setPurchaserOptions] = useState<PurchaserOption[]>(
    [],
  );
  const [purchasersLoading, setPurchasersLoading] = useState(false);
  const [portalKeyOptions, setPortalKeyOptions] = useState<PortalKeyOption[]>(
    [],
  );
  const [portalKeysLoading, setPortalKeysLoading] = useState(false);
  const [credentialToDelete, setCredentialToDelete] =
    useState<ConfigPortalCredential | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [metadataPreview, setMetadataPreview] =
    useState<ConfigPortalCredential | null>(null);

  const repository = useMemo(() => new ConfigPortalCredentialRepository(), []);
  const createUseCase = useMemo(
    () => new CreateConfigPortalCredentialUseCase(repository),
    [repository],
  );
  const updateUseCase = useMemo(
    () => new UpdateConfigPortalCredentialUseCase(repository),
    [repository],
  );
  const deleteUseCase = useMemo(
    () => new DeleteConfigPortalCredentialUseCase(repository),
    [repository],
  );
  const portalKeysUseCase = useMemo(
    () => new GetPortalKeysByPurchaserKeyUseCase(repository),
    [repository],
  );
  const purchasersUseCase = useMemo(
    () =>
      new GetMasterDataPurchasersUseCase(
        new MasterDataPurchaserRepository(
          ConfigGraphQlAdapter.getInstance().getClient(),
        ),
      ),
    [],
  );

  const loadPurchasers = useCallback(async () => {
    setPurchasersLoading(true);
    try {
      const result = await purchasersUseCase.execute(
        new GetMasterDataPurchasersDTO({
          search: null,
          includeInactive: false,
        }),
        brandKey,
      );
      setPurchaserOptions(
        result.items.map((item) => ({
          purchaserId: item.purchaserId,
          purchaserName: item.purchaserName,
          purchaserKey: item.purchaserKey,
        })),
      );
    } catch (err) {
      errorToast({
        content: getConfigSaveErrorMessage(
          err,
          "Failed to load purchasers.",
          "Failed to load purchasers. Please try again later.",
        ),
      });
    } finally {
      setPurchasersLoading(false);
    }
  }, [brandKey, errorToast, purchasersUseCase]);

  const loadPortalKeys = useCallback(
    async (purchaserKey: string | null) => {
      if (!purchaserKey) {
        setPortalKeyOptions([]);
        return;
      }
      setPortalKeysLoading(true);
      try {
        const keys: PurchaserPortalKey[] =
          await portalKeysUseCase.execute(purchaserKey);
        setPortalKeyOptions(
          keys.map((key) => ({
            portalKey: key.portalKey,
            portalKeyName: key.portalKeyName,
          })),
        );
      } catch (err) {
        setPortalKeyOptions([]);
        errorToast({
          content: getConfigSaveErrorMessage(
            err,
            "Failed to load portal keys.",
            "Failed to load portal keys. Please try again later.",
          ),
        });
      } finally {
        setPortalKeysLoading(false);
      }
    },
    [errorToast, portalKeysUseCase],
  );

  useEffect(() => {
    if (modalMode === "create") {
      void loadPurchasers();
      setPortalKeyOptions([]);
    }
  }, [loadPurchasers, modalMode]);

  const initialValues = useMemo((): PortalCredentialAddEditFormValues => {
    if (modalMode === "edit" && editingCredential) {
      return {
        purchaserId: editingCredential.purchaserId,
        portalKey: editingCredential.portalKey ?? "",
        portalUserName: editingCredential.portalUserName,
        portalPassword: editingCredential.portalPassword ?? "",
        isActive: editingCredential.isActive,
        metadata: formatJsonForTextarea(editingCredential.metadata),
      };
    }
    return EMPTY_FORM_VALUES;
  }, [editingCredential, modalMode]);

  const closeModal = useCallback(() => {
    closeModalState(form);
    setPortalKeyOptions([]);
  }, [closeModalState, form]);

  const handlePurchaserChange = useCallback(
    (_purchaserId: number | null, purchaserKey: string | null) => {
      void loadPortalKeys(purchaserKey);
    },
    [loadPortalKeys],
  );

  const handleSubmit = useCallback(
    async (values: PortalCredentialAddEditFormValues) => {
      setIsSaving(true);
      try {
        let metadata: unknown | null = null;
        try {
          metadata = parseOptionalJsonField(values.metadata ?? "", "Metadata");
        } catch (parseErr) {
          errorToast({
            content:
              parseErr instanceof Error
                ? parseErr.message
                : "Metadata must be valid JSON.",
          });
          return;
        }

        // Treat empty object from untouched JsonEditor as null
        if (
          metadata != null &&
          typeof metadata === "object" &&
          !Array.isArray(metadata) &&
          Object.keys(metadata as object).length === 0
        ) {
          metadata = null;
        }

        if (modalMode === "edit" && editingCredential) {
          await updateUseCase.execute(
            new UpdateConfigPortalCredentialDTO({
              id: editingCredential.id,
              portalUserName: values.portalUserName.trim(),
              portalPassword: values.portalPassword?.trim() || null,
              isActive: Boolean(values.isActive),
              metadata,
            }),
          );
        } else {
          if (values.purchaserId == null || !values.portalKey.trim()) {
            throw new Error("Purchaser and portal key are required.");
          }
          await createUseCase.execute(
            new CreateConfigPortalCredentialDTO({
              purchaserId: values.purchaserId,
              portalKey: values.portalKey.trim(),
              portalUserName: values.portalUserName.trim(),
              portalPassword: values.portalPassword?.trim() || null,
              isActive: Boolean(values.isActive),
              metadata,
            }),
          );
        }

        successToast({ content: "Portal credential saved successfully" });
        invalidateConfigListCacheScopes(["portal-credentials"]);
        if (modalMode !== "edit") {
          setPage(1);
        }
        closeModal();
        refetch();
      } catch (err) {
        errorToast({
          content: getConfigSaveErrorMessage(
            err,
            "Failed to save portal credential. Please try again.",
            "Failed to save portal credential. Please try again later.",
          ),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeModal,
      createUseCase,
      editingCredential,
      errorToast,
      modalMode,
      refetch,
      setIsSaving,
      setPage,
      successToast,
      updateUseCase,
    ],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!credentialToDelete) return;
    setIsDeleting(true);
    try {
      await deleteUseCase.execute(
        new DeleteConfigPortalCredentialDTO({ id: credentialToDelete.id }),
      );
      successToast({ content: "Portal credential deleted successfully" });
      invalidateConfigListCacheScopes(["portal-credentials"]);
      setCredentialToDelete(null);
      refetch();
    } catch (err) {
      errorToast({
        content: getConfigSaveErrorMessage(
          err,
          "Failed to delete portal credential. Please try again.",
          "Failed to delete portal credential. Please try again later.",
        ),
      });
    } finally {
      setIsDeleting(false);
    }
  }, [
    credentialToDelete,
    deleteUseCase,
    errorToast,
    refetch,
    successToast,
  ]);

  return (
    <>
      <ConfigWritableListLayout
        title="Portal Credentials"
        subtitle={formatConfigRecordCount(loading, total)}
        searchPlaceholder="Search portal credentials"
        search={search}
        setSearch={setSearch}
        addButtonLabel="Add Portal Credential"
        onAdd={openCreateModal}
        loading={loading}
      >
        <ConfigPortalCredentialsTable
          credentials={credentials}
          search={search}
          setSearch={setSearch}
          setPage={setPage}
          onEdit={openEditModal}
          onDelete={setCredentialToDelete}
          onViewMetadata={setMetadataPreview}
        />
      </ConfigWritableListLayout>

      <ConfigAddEditModal
        mode={modalMode}
        createTitle="Add Portal Credential"
        editTitle="Edit Portal Credential"
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        form={form}
        onClose={closeModal}
      >
        <ConfigPortalCredentialAddEditForm
          form={form}
          mode={modalMode ?? "create"}
          initialValues={initialValues}
          purchaserOptions={purchaserOptions}
          purchasersLoading={purchasersLoading}
          portalKeyOptions={portalKeyOptions}
          portalKeysLoading={portalKeysLoading}
          lockedPurchaserName={editingCredential?.purchaserName}
          lockedPortalKey={editingCredential?.portalKey ?? undefined}
          onPurchaserChange={handlePurchaserChange}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </ConfigAddEditModal>

      <Modal
        isOpen={credentialToDelete != null}
        onClose={() => {
          if (!isDeleting) setCredentialToDelete(null);
        }}
        size="small"
        closable={!isDeleting}
        headerContent={
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Delete portal credential
          </Heading>
        }
        footerContent={
          <Flex gap={0.5} className="flex-1" justifyContent="space-between">
            <Button
              size="small"
              color="grey"
              isDisabled={isDeleting}
              onPress={() => setCredentialToDelete(null)}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              isLoading={isDeleting}
              isDisabled={isDeleting}
              onPress={() => {
                void handleConfirmDelete();
              }}
            >
              Delete
            </Button>
          </Flex>
        }
      >
        <Paragraph className="m-0 text-grey-600">
          Soft-delete portal credentials for{" "}
          <strong>{credentialToDelete?.purchaserName}</strong>
          {credentialToDelete?.portalKey
            ? ` (${credentialToDelete.portalKey})`
            : ""}
          ?
        </Paragraph>
      </Modal>

      <MasterDataJsonPreviewModal
        isOpen={metadataPreview != null}
        title={
          metadataPreview
            ? `Metadata — ${metadataPreview.purchaserName}`
            : "Metadata"
        }
        value={metadataPreview?.metadata}
        onClose={() => setMetadataPreview(null)}
      />
    </>
  );
};
